<script setup>
import _ from 'lodash'
import numeral from 'numeral'
import moment from 'moment'
import { h, inject, watch, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import DataTable from '@/components/ui/data-table/DataTableServerSide.vue'
import DataTableSortableColumnHeader from '@/components/ui/data-table/DataTableSortableColumnHeader.vue'
import ContentInfoCell from './content/ContentInfoCell.vue'
import ExternalLinkCell from './content/ExternalLinkCell.vue'
import AppIcon from '@/components/AppIcon.vue'

const axios = inject('axios')
const filters = inject('filters')
const store = useStore()
const router = useRouter()
const route = useRoute()

const props = defineProps({
    platformId: {
        type: String,
        required: true,
    },
    platformUserId: {
        type: String,
        required: true,
    },
    platformUserName: {
        type: String,
        required: true,
    },
    contentType: {
        type: String,
    },
    dayRange: {
        type: String,
    },
    games: {
        type: Object,
        default: () => {},
    },
})

const columns = computed(() => {
    const standardColumns = [
        {
            id : 'title',
            accessorKey: 'title',
            meta: {
                title: 'Title',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row }) => {
                return h(ContentInfoCell, {
                    data: row.original,
                    platformId: props.platformId,
                    contentType: props.contentType,
                    link: row.original?.link ?? null,
                    row,
                })
            },
            enableSorting: true,
            exportTransformer: (item) => ({
                Title: _.get(item, 'title'),
            }),
            isEnabled: () => true,
        },
        {
            id : 'games',
            accessorKey: 'games',
            accessorFn: (row) => _.map(row.items, 'game_id'),
            header: () => null,
            cell: () => null,
            enableHiding: false,
        },
        {
            id : 'tags',
            accessorKey: 'tags',
            header: () => null,
            cell: () => null,
            enableHiding: false,
        },
        {
            id : 'link',
            accessorKey: 'link',
            meta: {
                title: 'Link',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h(ExternalLinkCell, {
                    link: row.getValue(column.id),
                })
            },
            enableSorting: false,
            isEnabled: () => ['youtube', 'tiktok', 'twitter', 'instagram'].includes(props.platformId),
        },
        {
            id : 'content_sub_type',
            accessorKey: 'content_sub_type',
            meta: {
                title: 'Type',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                let value = row.getValue(column.id)

                if (value === 'video') {
                    value = 'reel'
                }

                return h('div', { class: 'whitespace-nowrap capitalize' }, value)
            },
            enableSorting: true,
            isEnabled: () => ['instagram'].includes(props.platformId),
        },

        {
            id : 'published_at',
            accessorKey: 'published_at',
            meta: {
                title: 'Posted At',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h(
                    'div',
                    { class: 'whitespace-nowrap' },
                    moment(row.getValue(column.id)).format('MMM Do, YY @ h:mm A'),
                )
            },
            enableSorting: true,
            isEnabled: () => props.contentType !== 'stream',
            exportTransformer: (item) => ({
                'Posted At': item.published_at ? moment(item.published_at).format('YYYY-MM-DD hh:mm:ss') : ''
            })
        },

        {
            id : 'stream.started_at',
            accessorKey: 'stream.started_at',
            meta: {
                title: 'Started At',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h(
                    'div',
                    { class: 'whitespace-nowrap' },
                    moment(row.getValue(column.id)).format('MMM Do, YY @ h:mm A'),
                )
            },
            enableSorting: true,
            isEnabled: () => props.contentType === 'stream',
            exportTransformer: (item) => ({
                'Posted At': item?.stream?.started_at ? moment(item.stream.started_at).format('YYYY-MM-DD hh:mm:ss') : ''
            })
        },
        {
            id : 'stream.ended_at',
            accessorKey: 'stream.ended_at',
            meta: {
                title: 'Ended At',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (!row.getValue(column.id)) return h('div', '---')
                return h(
                    'div',
                    { class: 'whitespace-nowrap' },
                    moment(row.getValue(column.id)).format('MMM Do, YY @ h:mm A'),
                )
            },
            enableSorting: true,
            isEnabled: () => props.contentType === 'stream',
            exportTransformer: (item) => ({
                'Ended At': item?.stream?.ended_at ? moment(item.stream.ended_at).format('YYYY-MM-DD hh:mm:ss') : ''
            })
        },
        {
            id : 'duration_seconds',
            accessorFn: (row) => row.duration_seconds * 1000,
            meta: {
                title: props.contentType === 'stream' ? 'Hours Streamed' : 'Duration',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (!row.getValue(column.id)) return h('div', '---')
                return h('div', filters.humanizeDurationShort(row.getValue(column.id)))
            },
            exportTransformer: (item) => ({
                'Duration Seconds': _.get(item, 'duration_seconds') ? _.get(item, 'duration_seconds') : 0,
            }),
            enableSorting: true,
            isEnabled: () => ['stream', 'video', 'short'].includes(props.contentType),
        },
        {
            id :  'statistics.avg_viewers',
            accessorFn: (row) => row?.statistics?.avg_viewers ?? 0,
            meta: {
                sortKey: 'statistics.avg_viewers',
                title: 'Avg Viewers',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['stream'].includes(props.contentType) && ['twitch'].includes(props.platformId),
        },
        {
            id : 'statistics.peak_viewers.value',
            accessorFn: (row) => row?.statistics?.peak_viewers?.value ?? 0,
            meta: {
                title: 'Peak Viewers',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['stream'].includes(props.contentType) && ['twitch'].includes(props.platformId),
        },
        {
            id : 'statistics.minutes_watched',
            accessorFn: (row) => row.statistics?.minutes_watched / 60,
            meta: {
                title: 'Hours Watched',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['stream'].includes(props.contentType) && ['twitch'].includes(props.platformId),
        },

        {
            id : 'statistics.views',
            accessorFn: (row) => row?.statistics?.views ?? 0,
            meta: {
                title: 'Views',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (props.platformId === 'instagram' && row?.original?.is_like_and_view_counts_disabled) {
                    return h('div', { class: 'flex items-center gap-2', title : 'View counts have been disabled on this post.' }, [
                        h(AppIcon, {
                            class: 'mr-1.5 h-3 w-3',
                            name: 'eye-slash'
                        }),
                    ])
                }
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => true,
        },

        {
            id : 'statistics.avg_commenters',
            accessorFn: (row) => row?.statistics?.avg_commenters ?? 0,
            meta: {
                title: 'Avg Chatters',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['stream'].includes(props.contentType) && ['twitch'].includes(props.platformId),

        },
        {
            id : 'statistics.avg_comments',
            accessorFn: (row) => row?.statistics?.avg_comments ?? 0,
            meta: {
                title: 'Avg Comments',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['stream'].includes(props.contentType) && ['twitch'].includes(props.platformId),
        },
        {
            id : 'statistics.comments',
            accessorFn: (row) => row?.statistics?.comments ?? 0,
            meta: {
                title: 'Comments',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (props.platformId === 'instagram' && row?.original?.is_comments_disabled) {
                    return h('div', { class: 'flex items-center gap-2', title : 'Comments have been disabled on this post.' }, [
                        h(AppIcon, {
                            class: 'mr-1.5 h-3 w-3',
                            name: 'eye-slash'
                        }),
                    ])
                }
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => {
                // Hide this column only if ALL content in the current view is Instagram stories
                if (props.platformId === 'instagram' && props.contentType === 'story') {
                    return false
                }
                return ['twitch', 'youtube', 'twitter', 'instagram'].includes(props.platformId)
            },
        },

        {
            id : 'statistics.likes',
            accessorFn: (row) => row?.statistics?.likes ?? 0,
            meta: {
                title: 'Likes',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (props.platformId === 'instagram' && row?.original?.is_like_and_view_counts_disabled) {
                    return h('div', { class: 'flex items-center gap-2', title : 'Like counts have been disabled on this post.' }, [
                        h(AppIcon, {
                            class: 'mr-1.5 h-3 w-3',
                            name: 'eye-slash'
                        }),
                    ])
                }
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => {
                // Hide this column only if ALL content in the current view is Instagram stories
                if (props.platformId === 'instagram' && props.contentType === 'story') {
                    return false
                }
                return ['youtube', 'tiktok', 'twitter', 'instagram'].includes(props.platformId)
            },
        },
        {
            id : 'statistics.shares',
            accessorFn: (row) => row?.statistics?.shares ?? 0,
            meta: {
                title: 'Shares',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => {
                if (props.platformId === 'instagram') {
                    return props.contentType === 'video'
                }
                return ['tiktok', 'twitter'].includes(props.platformId)
            },
        },
        {
            id : 'statistics.quotes',
            accessorFn: (row) => row?.statistics?.quotes ?? 0,
            meta: {
                title: 'Quotes',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0,0'))
            },
            enableSorting: true,
            isEnabled: () => ['twitter'].includes(props.platformId),
        },

        {
            id : 'statistics.engagement_rate',
            accessorFn: (row) => row?.statistics?.engagement_rate ?? 0,
            meta: {
                title: 'Engagement Rate',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h('div', numeral(row.getValue(column.id)).format('0.0%'))
            },
            enableSorting: true,
            isEnabled: () => {
                // Hide for YouTube streams and Instagram stories
                if (props.platformId === 'youtube' && props.contentType === 'stream') {
                    return false
                }
                if (props.platformId === 'instagram' && props.contentType === 'story') {
                    return false
                }
                return true
            },
            exportTransformer: (item) => ({
                'Engagement Rate': numeral(_.get(item, 'statistics.engagement_rate', 0)).format('0.0%'),
            }),
        },

        {
            id : 'vod.views',
            accessorFn: (row) => row?.vod?.views ?? '',
            meta: {
                title: 'Vod Views',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {

                let formattedValue = ''
                if (row.getValue(column.id)) {
                    formattedValue = numeral(row.getValue(column.id)).format('0,0')
                }
                return h('div', formattedValue)
            },
            enableSorting: false,
            isEnabled: () => {
                return props.platformId === 'twitch'
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'Vod': _.get(item, 'vod.url', ''),
            }),
        },

        {
            id : 'vod.url',
            accessorFn: (row) => row?.vod?.url ?? '',
            meta: {
                title: 'Vod',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (row.getValue(column.id)) {
                    return h(ExternalLinkCell, {
                        link: row.getValue(column.id),
                    })
                }
                return h('div', '-')
            },
            enableSorting: false,
            isEnabled: () => {
                return props.platformId === 'twitch'
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'Vod': _.get(item, 'vod.url', ''),
            }),
        },

        {
            id : 'is_sponsored',
            accessorKey: 'is_sponsored',
            meta: {
                title: 'Sponsored Status',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                return h(
                    'span',
                    { class: row.getValue(column.id) === true ? 'rounded bg-emerald-100 text-emerald-600 px-2 py-0.5' : 'rounded bg-slate-100 text-slate-600 px-2 py-0.5' },
                    row.getValue(column.id) === true ? 'Sponsored' : 'Organic',
                )
            },
            enableSorting: true,
            isEnabled: () => true,
        },
        {
            id :  'id',
            accessorKey: 'id',
            meta: {
                title: 'Id',
            },
            header: ({ column }) =>
                h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
            cell: ({ row, column }) => {
                if (props.platformId === 'twitch' && row.getValue(column.id)?.includes('-')) {
                    return h('div', '')
                }
                return h('div', row.getValue(column.id))
            },
            exportTransformer: (item) => ({
                Title: props.platformId === 'twitch' && _.get(item, 'id')?.includes('-') ? '' : _.get(item, 'id'),
            }),
            enableSorting: false,
            isEnabled: () => true,
        },

    ].filter((column) => (column?.isEnabled ? column.isEnabled() : true))

    return standardColumns
})

const toolbar = ref({
    placeholder: 'Filter by title...',
    columnToFilter: 'title', // Assuming 'tag' is the main field to filter on
    filterValue: '',
})

const rowCount = ref(-1)

const getPreferences = () => {
    return _.get(store, `state.preferences.tables.creatorContent.${props.platformId}`)
    // return store?.state?.preferences?.tables['creator-content']
}

const pagination = ref(
    (() => {
        return {
            pageIndex: 0,
            pageSize: getPreferences()?.paging?.pageSize ? getPreferences().paging.pageSize : 10,
        }
    })(),
)
const sorting = ref(
    (() => {
        return getPreferences()?.sorting
            ? getPreferences().sorting
            : [
                  {
                      id: props.platformId === 'twitch' ? 'stream.started_at' : 'published_at',
                      desc: true,
                  },
              ]
    })(),
)

const filtering = ref([])

const data = ref([])
const isLoaded = ref(false)

const fetchData = async () => {
    isLoaded.value = false

    const endpoint = `/platforms/${props.platformId}/${props.platformUserId}/content`

    try {
        const requestParams = {
            content_type: props.contentType,
            day_range: props.dayRange,
            limit: pagination.value.pageSize,
            offset: pagination.value.pageSize * pagination.value.pageIndex,
            sort_field: sorting.value?.[0]?.id,
            sort_direction: sorting.value?.[0]?.desc === true ? -1 : 1,
            text_filter: toolbar.value.filterValue ? toolbar.value.filterValue : undefined,
        }

        if (filtering?.value?.length > 0) {
            _.forEach(filtering.value, ({ id, value }) => {
                requestParams[id] = value
            })
        }

        const response = await axios.get(endpoint, {
            params: requestParams,
        })

        data.value = response.data.payload.content
        rowCount.value = response.data.payload.count
        isLoaded.value = true
    } catch (error) {
        console.error('Error fetching tags data:', error)
        data.value = []
        isLoaded.value = true
    }
}

const tags = ref([])
const isTagsLoaded = ref(false)

const fetchTags = async () => {
    isTagsLoaded.value = false
    const endpoint = `/platforms/${props.platformId}/${props.platformUserId}/tags`

    try {
        const response = await axios.get(endpoint, {
            params: {
                content_type: props.contentType,
                day_range: props.dayRange,
                limit: 100,
                offset: 0,
            },
        })
        tags.value = _.filter(response.data.payload.tags, (item) => item._id)
        isTagsLoaded.value = true
    } catch (error) {
        console.error('Error fetching tags data:', error)
        tags.value = []
        isTagsLoaded.value = true
    }
}

const filterFields = computed(() => [
    {
        label: 'Tags',
        value: 'tags',
        options: _(tags.value)
            .map((item) => {
                return {
                    label: item.tag,
                    value: item.tag,
                    count: item?.count,
                    withCount: true,
                }
            })
            .orderBy(['count'], ['desc'])
            .value(),
    },
    {
        label: 'Games',
        value: 'games',
        options: _(props?.games)
            .map((game, gameKey) => ({
                label: game.name,
                value: gameKey,
                count: game.count,
                withCount: true,
            }))
            .orderBy(['count'], ['desc'])
            .value(),
    },
    {
        label: 'Sponsored Status',
        value: 'is_sponsored',
        options: [
            { label: 'Organic', value: 'organic' },
            { label: 'Sponsored', value: 'sponsored' },
        ],
    },
])

if (route.query['profile.tabs.primary.filters']) {
    filtering.value = []

    const urlParameterFilters = Array.isArray(route.query['profile.tabs.primary.filters'])
        ? route.query['profile.tabs.primary.filters']
        : [route.query['profile.tabs.primary.filters']]

    _.forEach(urlParameterFilters, (urlParameterFilter) => {
        console.log(urlParameterFilter, decodeURIComponent(urlParameterFilter))

        const [id, value] = decodeURIComponent(urlParameterFilter).split(':')

        // Only append a filter if it's an option
        const values = value?.includes(',') ? value.split(',') : [value]

        filtering.value.push({
            id,
            value: values,
        })
    })
}

// Anytime the filters change, persist it to the url
watch(
    () => [filtering],
    () => {
        router.push({
            query: {
                ...route.query,
                'profile.tabs.secondary.filters': _.map(filtering.value, ({ id, value }) => {
                    return `${encodeURIComponent(id)}:${encodeURIComponent(value)}`
                }),
            },
        })
    },
    {
        immediate: false,
        deep: true,
    },
)

watch(
    () => [
        props.platformId,
        props.platformUserId,
        props.contentType,
        props.dayRange,
        pagination,
        sorting,
        toolbar,
        filtering,
    ],
    fetchData,
    {
        immediate: true, // This ensures fetchData is called on initial render too
        deep: true,
    },
)

// Anytime the content type or day range are changed, clear filters
watch(
    () => [props.contentType, props.dayRange],
    () => {
        filtering.value = []
    },
    {
        immediate: false,
    },
)

watch(
    () => [props.platformId, props.platformUserId, props.contentType, props.dayRange],
    () => {
        fetchTags()
    },
    {
        immediate: true,
    },
)

const handlePaginationUpdate = (newPagination) => {
    pagination.value.pageSize = newPagination.pageSize
    pagination.value.pageIndex = newPagination.pageIndex
}

const handleSortingUpdate = (newSorting) => {
    sorting.value = [...newSorting]
}

const handleTextFilterUpdate = (newTextFilterValue) => {
    toolbar.value.filterValue = newTextFilterValue
    pagination.value.pageIndex = 0
}

const handleFilteringUpdate = (newFilteringValue) => {
    filtering.value = [...newFilteringValue]
    pagination.value.pageIndex = 0
}

const debouncedTextFilterUpdate = _.debounce(handleTextFilterUpdate, 250)
</script>

<template>
    <DataTable
        :columns="columns"
        :data="data"
        :toolbar="toolbar"
        :is-loaded="isLoaded"
        :row-count="rowCount"
        :is-server-side="true"
        :pagination="pagination"
        :filter-fields="filterFields"
        :filtering="filtering"
        table-header-cell-class="first:pl-4 whitespace-nowrap"
        table-cell-class="px-3 py-0 text-sm data-[subrow=true]:bg-slate-100 h-[50px]"
        :preferences-id="`creatorContent.${props.platformId}`"
        @update:pagination="handlePaginationUpdate"
        @update:sorting="handleSortingUpdate"
        @update:text-filter="debouncedTextFilterUpdate"
        @update:filtering="handleFilteringUpdate"
    >
        <template #loading-skeleton>
            <tr v-for="rowIndex in pagination.pageSize" :key="rowIndex">
                <td v-for="colIndex in columns?.length" :key="colIndex" class="h-[50px]">
                    <div class="my-0.5 h-full w-full animate-pulse rounded bg-slate-100" />
                </td>
            </tr>
        </template>
    </DataTable>
</template>
